import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBytes } from './format.ts';

/** 'moment' — момент, клип из которого ещё не нарезан: файлов у него нет,
 *  освобождать нечего, поэтому чекбокс освобождения не показываем. */
export type ArchiveKind = 'clip' | 'variant' | 'film' | 'moment';

interface Props {
  kind: ArchiveKind;
  /** Сколько дней файл проживёт после архивации. Приходит с бэкенда — не хардкодить. */
  freeAfterDays: number;
  /** Сколько байт освободится. null = размер неизвестен (строку «Освободит …» не показываем). */
  sizeBytes: number | null;
  /** clip: сколько вариантов останутся активными. film: сколько вариантов у его клипов. */
  variantsCount?: number;
  /** film: сколько клипов останутся активными. */
  clipsCount?: number;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (opts: { freeFiles: boolean }) => void;
}

/** Чекбокс освобождения: для клипа и варианта включён, для фильма — выключен
 *  (исходник невосстановим, осознанное действие). */
const FREE_BY_DEFAULT: Record<ArchiveKind, boolean> = {
  clip: true,
  variant: true,
  film: false,
  moment: false,
};

/**
 * Одна модалка на три сущности. Показывает ПОСЛЕДСТВИЯ архивации, а не
 * спрашивает про каскад: каскадов нет — потомки остаются активными.
 */
export function ArchiveDialog({
  kind,
  freeAfterDays,
  sizeBytes,
  variantsCount = 0,
  clipsCount = 0,
  busy = false,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  const [freeFiles, setFreeFiles] = useState(FREE_BY_DEFAULT[kind]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const size = formatBytes(sizeBytes);
  const frees = size ? t('archive.dialog.freesSize', { size }) : null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          width: 520,
          maxWidth: '94vw',
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            margin: 0,
            padding: '16px 20px',
            fontSize: 16,
            borderBottom: '1px solid var(--line)',
          }}
        >
          {t(`archive.dialog.${kind}.title`)}
        </h2>

        <div
          style={{
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div className="note">{consequence(kind, { variantsCount, clipsCount, t })}</div>

          {/* У момента без клипа файлов нет — освобождать нечего. */}
          {kind !== 'moment' && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={freeFiles}
                onChange={(e) => setFreeFiles(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span style={{ fontSize: 13 }}>
                <b style={{ display: 'block' }}>
                  {t(`archive.dialog.${kind}.freeLabel`, { days: freeAfterDays })}
                </b>
                {[frees, hint(kind, t)].filter(Boolean).join(' ')}
              </span>
            </label>
          )}

          {kind === 'film' && <div className="note err">{t('archive.dialog.film.danger')}</div>}
        </div>

        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--line)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: 'var(--bg-soft)',
          }}
        >
          <button type="button" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => onConfirm({ freeFiles })}
          >
            {t('archive.dialog.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

type TFn = ReturnType<typeof useTranslation>['t'];

/** Что именно случится. Числа потомков — с бэкенда, склонения — через плюрали i18next. */
function consequence(
  kind: ArchiveKind,
  ctx: { variantsCount: number; clipsCount: number; t: TFn },
): ReactNode {
  const { variantsCount, clipsCount, t } = ctx;

  if (kind === 'variant') return t('archive.dialog.variant.note');
  if (kind === 'moment') return t('archive.dialog.moment.note');

  if (kind === 'clip') {
    if (variantsCount === 0) return t('archive.dialog.clip.noteNone');
    return (
      <>
        {t('archive.dialog.clip.noteLead')}{' '}
        <b>{t('archive.dialog.clip.noteBold', { count: variantsCount })}</b>{' '}
        {t('archive.dialog.clip.noteTail', { count: variantsCount })}
      </>
    );
  }

  // film
  if (clipsCount === 0 && variantsCount === 0) return t('archive.dialog.film.noteNone');
  const children = [
    t('archive.dialog.film.clipsPart', { count: clipsCount }),
    t('archive.dialog.film.variantsPart', { count: variantsCount }),
  ].join(` ${t('archive.dialog.and')} `);
  return (
    <>
      {t('archive.dialog.film.noteLead')}{' '}
      <b>
        {children} {t('archive.dialog.film.stayActive', { count: clipsCount + variantsCount })}
      </b>
      . {t('archive.dialog.film.noteTail')}
    </>
  );
}

/** Пояснение под чекбоксом: чем обернётся освобождение файла. У фильма его нет —
 *  вместо него отдельный красный блок о невосстановимости исходника. */
function hint(kind: ArchiveKind, t: TFn): string {
  return kind === 'film' ? '' : t(`archive.dialog.${kind}.freeHint`);
}
